import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AlertOctagon, Archive, ArchiveRestore, Bot, Check, Copy, ExternalLink, GitBranch, Keyboard, LayoutTemplate, Link2, Lock, MoreHorizontal, Pencil, Plus, Rocket, ShieldAlert, Trash2, X } from 'lucide-react';
import { completesWork } from '@vwt/domain';
import { WORK_TYPES, type AcceptanceCriterion, type RelationKind, type WorkAction, type WorkItemView } from '@vwt/contracts';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  Input,
  Kbd,
  NativeSelect,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  Tooltip,
} from '@vwt/ui';
import { api, newIdempotencyKey, POLL_ACTIVE, useApiMutation, useApiQuery } from '../../lib/api.js';
import { useUnsavedChanges } from '../../lib/unsaved.js';
import { useConflictAwareSave } from '../../components/conflict-dialog.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { AgentCreatedIcon, DeliveryBadge, DueLabel, KeyLabel, PhaseBadge, PriorityLabel, ProgressPill, VisibilityBadge, NoEvidenceBadge, PointsBadge } from '../../components/badges.js';
import { PrincipalPicker, PropertyRow, QueryState, SectionTitle, TimeAgo, UserChip } from '../../components/common.js';
import { CommentsPanel } from '../../components/comments.js';
import { AttachmentsPanel } from '../../components/attachments.js';
import { MarkdownEditor, MarkdownView } from '../../components/markdown.js';
import { OriginalLanguage, TranslatableMarkdown, TranslatableText, TranslateToggle, TranslationScope, useTranslatedCriteria } from '../../components/translation.js';
import { VisibilityDialog } from '../../components/visibility.js';
import { RepositoryDialog } from '../../components/repository-dialog.js';
import { Timeline } from '../../components/timeline.js';
import { formatCalendarDate, formatDateTime } from '../../lib/format.js';
import { useHotkeys } from '../../lib/hotkeys.js';
import { branchName } from '../../lib/branch.js';
import { useMe } from '../../lib/session.js';
import { DeliveryPanel } from './delivery-panel.js';
import { CodePanel } from './code-panel.js';
import { CreateWorkDialog } from './create-work.js';
import { LabelBadge, LabelManagerDialog, useLabelCatalog } from '../../components/labels.js';
import { SaveTemplateDialog } from '../../components/templates.js';
import { ItemRef } from '../../components/item-ref.js';
import { DateInput } from '../../components/date-input.js';
import { FieldEditor, fieldsFor, FieldValue, useCustomFields } from '../../components/custom-fields.js';
import { DocumentBacklinks } from '../documents/document-backlinks.js';
import { causeInput, CauseFields, CauseNotInformedBanner, CauseValue, emptyCause, WorkSearchList } from './defect-cause.js';

const PRIMARY_ACTIONS: WorkAction[] = ['start', 'request_review', 'complete_deliverable', 'approve_deliverable', 'request_changes', 'reopen'];

/** Work item detail by display key (VAM-12) or UUID. */
export function WorkDetail({ id, inDrawer = false }: { id: string; inDrawer?: boolean }) {
  return <ItemRef value={id} kind="WORK_ITEM">{(uuid) => <WorkDetailById id={uuid} inDrawer={inDrawer} />}</ItemRef>;
}

function WorkDetailById({ id, inDrawer }: { id: string; inDrawer: boolean }) {
  const { t } = useTranslation();
  const query = useApiQuery('getWorkItem', { params: { id } }, { refetchInterval: POLL_ACTIVE });
  const item = query.data?.data;
  return (
    <QueryState
      query={query}
      skeleton={
        <div className="flex flex-col gap-3">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-40 w-full" />
        </div>
      }
    >
      {item ? (
        <TranslationScope key={item.id}>
          <WorkDetailBody item={item} inDrawer={inDrawer} />
        </TranslationScope>
      ) : (
        <p className="p-6 text-sm">{t('errors.notFound')}</p>
      )}
    </QueryState>
  );
}

function WorkDetailBody({ item, inDrawer }: { item: WorkItemView; inDrawer: boolean }) {
  const { t } = useTranslation();
  const can = (action: string) => item.availableActions.includes(action);
  const [tab, setTab] = useState('overview');
  const [transition, setTransition] = useState<WorkAction | null>(null);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [subItemOpen, setSubItemOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const prioritySelect = useRef<HTMLSelectElement | null>(null);
  const me = useMe();
  const update = useApiMutation('updateWorkItem', { invalidate: ['getWorkItem', 'listWorkItems'] });
  const follow = useApiMutation('followWorkItem', { invalidate: ['getWorkItem', 'listWorkItems'] });
  const archive = useApiMutation('archiveWorkItem', { invalidate: ['getWorkItem', 'listWorkItems'] });

  const { patch, dialog: conflictDialog } = useConflictAwareSave(
    item,
    async () => (await api.call('getWorkItem', { params: { id: item.id } }))!.data,
    (body, version) => update.mutateAsync({ params: { id: item.id }, body: { ...body, expectedVersion: version } as never, idempotencyKey: newIdempotencyKey() }),
  );

  const copy = (text: string, message: string) => {
    void navigator.clipboard.writeText(text);
    toast.success(message);
  };
  const copyKey = () => copy(item.key, t('work.detail.keyCopied', { key: item.key }));
  const copyBranch = () => {
    const branch = branchName(item);
    copy(branch, t('work.detail.branchCopied', { branch }));
  };
  const setArchived = async (archived: boolean) => {
    try {
      await archive.mutateAsync({ params: { id: item.id }, body: { expectedVersion: item.version, archived }, idempotencyKey: newIdempotencyKey() });
      toast.success(t(archived ? 'work.detail.archivedToast' : 'work.detail.restoredToast'));
    } catch (error) {
      toastError(error);
    }
  };

  // Item shortcuts (Plane-style); they never fire while typing in a field, nor while another
  // dialog or menu is open over this item (they would change the item behind it).
  const root = useRef<HTMLDivElement | null>(null);
  const hotkeys = useMemo(() => {
    const blocked = () =>
      !!document.querySelector('[role="menu"][data-state="open"], [role="listbox"]') ||
      [...document.querySelectorAll('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')].some((d) => !d.contains(root.current));
    const guard = (fn: () => void) => () => {
      if (!blocked()) fn();
    };
    return {
      p: guard(() => {
        if (!can('prioritize')) return;
        prioritySelect.current?.focus();
        prioritySelect.current?.showPicker?.();
      }),
      a: guard(() => can('assign') && setAssignOpen(true)),
      i: guard(() => can('assign') && void patch({ assigneeId: me.id })),
      l: guard(() => can('edit') && setLabelsOpen(true)),
      'mod+.': guard(copyKey),
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.id, item.version, item.availableActions.join(','), me.id],
  );
  useHotkeys(hotkeys);

  const transitions = item.availableActions.filter((a) => a.startsWith('transition:')).map((a) => a.slice('transition:'.length) as WorkAction);
  const primary = transitions.filter((a) => PRIMARY_ACTIONS.includes(a)).slice(0, 2);
  const secondary = transitions.filter((a) => !primary.includes(a));

  return (
    <div ref={root} className="@container flex h-full min-h-0 flex-col">
      <div className="border-b border-border px-5 pt-4 pb-3">
        <div className="mb-2 flex flex-wrap items-center gap-2 pr-8">
          <KeyLabel value={item.key} />
          {item.aliases.map((a) => (
            <Badge key={a} tone="outline">
              {a}
            </Badge>
          ))}
          <VisibilityBadge visibility={item.visibility} />
          {item.associationStatus === 'PROVISIONAL' ? (
            <Badge tone="warning">
              <Bot /> {t('status.association.PROVISIONAL')}
            </Badge>
          ) : null}
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm" aria-pressed={item.following} onClick={() => void follow.mutateAsync({ params: { id: item.id }, body: { following: !item.following } }).catch(toastError)}>
              {item.following ? <Check /> : <Plus />} {item.following ? t('requests.detail.following') : t('requests.detail.followers')}
            </Button>
            <TranslateToggle />
            <Tooltip content={t('common.copyLink')}>
              <Button
                variant="ghost"
                size="iconSm"
                aria-label={t('common.copyLink')}
                onClick={() => copy(`${window.location.origin}/work/${item.key}`, t('common.linkCopied'))}
              >
                <Link2 />
              </Button>
            </Tooltip>
            <Tooltip content={t('work.detail.copyBranch')}>
              <Button variant="ghost" size="iconSm" aria-label={t('work.detail.copyBranch')} onClick={copyBranch}>
                <GitBranch />
              </Button>
            </Tooltip>
            {inDrawer ? (
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/work/${item.key}`}>
                  <ExternalLink /> {t('common.openFullPage')}
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
        {item.hierarchy.ancestors.length ? (
          <nav aria-label={t('work.detail.parents')} className="mb-1 flex flex-wrap items-center gap-1 text-xs text-fg-subtle">
            {item.hierarchy.ancestors.map((a) => (
              <span key={a.id} className="flex items-center gap-1">
                <Link to={`/work/${a.key}`} className="hover:text-fg hover:underline" title={a.title}>
                  {a.key} <span className="hidden max-w-40 truncate align-bottom sm:inline-block">{a.title}</span>
                </Link>
                <span aria-hidden>›</span>
              </span>
            ))}
          </nav>
        ) : null}
        <EditableTitle item={item} canEdit={can('edit')} onSave={(title) => patch({ title })} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <PhaseBadge phase={item.phase} />
          <NoEvidenceBadge basis={item.completionBasis} />
          <PointsBadge weight={item.weight} />
          <AgentCreatedIcon item={item} />
          {item.completionPolicy === 'PRODUCTION' ? <DeliveryBadge state={item.deliveryState} /> : null}
          <DueLabel date={item.dueDate} phase={item.phase} />
          {item.blocked ? (
            <Badge tone="danger">
              <AlertOctagon /> {t('work.detail.blockedNote')}
            </Badge>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {primary.map((action, index) => (
              <Button key={action} size="sm" variant={index === 0 ? 'primary' : 'secondary'} onClick={() => setTransition(action)}>
                {t(`actions.work.${action}`)}
              </Button>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="iconSm" variant="ghost" aria-label="More">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {secondary.map((action) => (
                  <DropdownMenuItem key={action} destructive={action === 'cancel'} onSelect={() => setTransition(action)}>
                    {t(`actions.work.${action}`)}
                  </DropdownMenuItem>
                ))}
                {can('changeVisibility') ? (
                  <DropdownMenuItem onSelect={() => setVisibilityOpen(true)}>
                    <Lock /> {t('visibility.title')}
                  </DropdownMenuItem>
                ) : null}
                {secondary.length || can('changeVisibility') ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem onSelect={copyKey}>
                  <Copy /> {t('work.detail.copyKey')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={copyBranch}>
                  <GitBranch /> {t('work.detail.copyBranch')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setTemplateOpen(true)}>
                  <LayoutTemplate /> {t('templates.saveAs')}
                </DropdownMenuItem>
                {can('archive') ? (
                  <DropdownMenuItem onSelect={() => void setArchived(true)}>
                    <Archive /> {t('work.detail.archive')}
                  </DropdownMenuItem>
                ) : null}
                {can('unarchive') ? (
                  <DropdownMenuItem onSelect={() => void setArchived(false)}>
                    <ArchiveRestore /> {t('work.detail.unarchive')}
                  </DropdownMenuItem>
                ) : null}
                {can('delete') ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                      <Trash2 /> {t('work.detail.delete')}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {item.archivedAt ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-[var(--radius)] bg-muted px-3 py-2 text-xs text-fg-muted">
            <Archive className="size-3.5" />
            <span className="min-w-0 flex-1">
              {t('work.detail.archivedBanner', { time: formatDateTime(item.archivedAt) })}
            </span>
            {can('unarchive') ? (
              <Button size="sm" variant="secondary" loading={archive.isPending} onClick={() => void setArchived(false)}>
                <ArchiveRestore /> {t('work.detail.unarchive')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {item.associationStatus === 'PROVISIONAL' && !item.archivedAt ? <ProvisionalReview item={item} canReview={can('reviewAssociation')} /> : null}
        {item.defectCause === 'NOT_INFORMED' && item.phase === 'DONE' ? <CauseNotInformedBanner item={item} canEdit={can('recordCause')} /> : null}
        {item.completionPolicy === 'PRODUCTION' && item.phase !== 'DONE' && item.deliveryScopeStatus !== 'NONE' ? (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-fg-subtle">
            <Rocket className="size-3.5" /> {t(`status.scope.${item.deliveryScopeStatus}`)} · {t('status.policyHelp.PRODUCTION')}
          </p>
        ) : null}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto @4xl:grid-cols-[minmax(0,1fr)_288px]">
        <Tabs value={tab} onValueChange={setTab} className="min-w-0 px-5 py-3">
          <TabsList>
            <TabsTrigger value="overview">{t('work.detail.overview')}</TabsTrigger>
            <TabsTrigger value="activity">
              {t('work.detail.activity')}
              {item.commentCount ? <span className="rounded-full bg-muted px-1.5 text-xs">{item.commentCount}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="progress">{t('work.detail.progress')}</TabsTrigger>
            <TabsTrigger value="code">{t('work.detail.code')}</TabsTrigger>
            {item.completionPolicy === 'PRODUCTION' ? <TabsTrigger value="delivery">{t('work.detail.delivery')}</TabsTrigger> : null}
            <TabsTrigger value="history">{t('work.detail.history')}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="flex flex-col gap-6 pt-4">
            <Description item={item} canEdit={can('edit')} onSave={(descriptionMarkdown) => patch({ descriptionMarkdown })} />
            <Criteria item={item} canEdit={can('edit')} onSave={(acceptanceCriteria) => patch({ acceptanceCriteria })} />
            <AttachmentsPanel parentKind="WORK_ITEM" parentId={item.id} />
            {item.outcomeMarkdown ? (
              <section>
                <SectionTitle>{t('work.detail.outcome')}</SectionTitle>
                <TranslatableMarkdown entityKind="WORK_ITEM" entityId={item.id} field="outcomeMarkdown" source={item.outcomeMarkdown} sourceLocale={item.originalLocale} contentRevision={item.contentRevision} />
              </section>
            ) : null}
            <Blockers item={item} canEdit={can('block')} />
            <SubItems item={item} canEdit={can('relate')} onAdd={() => setSubItemOpen(true)} />
            <Relations item={item} canEdit={can('relate')} />
            <DocumentBacklinks targetKind="WORK_ITEM" targetId={item.id} />
            {item.linkedRequests.length ? (
              <section>
                <SectionTitle>{t('work.detail.linkedRequests')}</SectionTitle>
                <ul className="flex flex-col gap-1">
                  {item.linkedRequests.map((r) => (
                    <li key={r.id} className="flex items-center gap-2 text-sm">
                      <KeyLabel value={r.key} />
                      <Link to={`/requests/${r.key}`} className="truncate hover:underline">
                        {r.title}
                      </Link>
                      {r.required ? <Badge tone="outline">{t('requests.detail.required')}</Badge> : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </TabsContent>
          <TabsContent value="activity" className="pt-4">
            <CommentsPanel parentKind="WORK_ITEM" parentId={item.id} />
          </TabsContent>
          <TabsContent value="progress" className="pt-4">
            <ProgressList id={item.id} />
          </TabsContent>
          <TabsContent value="code" className="pt-4">
            <CodePanel item={item} />
          </TabsContent>
          {item.completionPolicy === 'PRODUCTION' ? (
            <TabsContent value="delivery" className="pt-4">
              <DeliveryPanel item={item} />
            </TabsContent>
          ) : null}
          <TabsContent value="history" className="pt-4">
            <Timeline id={item.id} kind="work" />
            <Episodes id={item.id} />
          </TabsContent>
        </Tabs>
        <Properties item={item} onPatch={patch} prioritySelect={prioritySelect} assignOpen={assignOpen} onAssignOpenChange={setAssignOpen} labelsOpen={labelsOpen} onLabelsOpenChange={setLabelsOpen} />
      </div>
      {transition ? <TransitionDialog item={item} action={transition} onClose={() => setTransition(null)} /> : null}
      {deleteOpen ? <DeleteWorkDialog item={item} onClose={() => setDeleteOpen(false)} /> : null}
      {templateOpen ? (
        <SaveTemplateDialog
          kind="WORK"
          defaultName={item.title}
          projectId={item.project?.id ?? null}
          onClose={() => setTemplateOpen(false)}
          payload={{
            title: item.title,
            type: item.type,
            priority: item.priority,
            ...(item.descriptionMarkdown.trim() ? { descriptionMarkdown: item.descriptionMarkdown } : {}),
            ...(item.acceptanceCriteria.length ? { acceptanceCriteria: item.acceptanceCriteria.map((c) => c.text) } : {}),
            ...(item.labels.length ? { labels: item.labels } : {}),
            ...(item.hierarchy.children.length ? { subItems: item.hierarchy.children.slice(0, 20).map((c) => ({ title: c.title, type: c.type })) } : {}),
          }}
        />
      ) : null}
      {subItemOpen ? (
        <CreateWorkDialog
          open
          onOpenChange={(open) => !open && setSubItemOpen(false)}
          defaults={{ parentId: item.id, parentKey: item.key, ...(item.project ? { projectId: item.project.id } : {}), ...(item.team ? { teamId: item.team.id } : {}) }}
        />
      ) : null}
      {conflictDialog}
      <VisibilityDialog open={visibilityOpen} onOpenChange={setVisibilityOpen} resource={{ kind: 'work', id: item.id }} />
    </div>
  );
}

function EditableTitle({ item, canEdit, onSave }: { item: WorkItemView; canEdit: boolean; onSave: (title: string) => Promise<boolean> }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.title);
  useUnsavedChanges(editing && value.trim() !== item.title);
  useEffect(() => {
    setValue(item.title);
  }, [item.title]);
  if (editing) {
    return (
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (value.trim() && value !== item.title && !(await onSave(value.trim()))) return;
          setEditing(false);
        }}
        className="flex items-center gap-2"
      >
        <Input value={value} onChange={(e) => setValue(e.target.value)} autoFocus maxLength={240} className="h-9 text-base font-semibold" onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), setEditing(false))} aria-label="Title" />
        <Button type="submit" size="iconSm" variant="primary" aria-label="Save">
          <Check />
        </Button>
      </form>
    );
  }
  return (
    <div className="group flex items-start gap-2">
      <div className="min-w-0">
        <TranslatableText as="h1" className="text-lg leading-snug font-semibold text-fg" entityKind="WORK_ITEM" entityId={item.id} field="title" source={item.title} sourceLocale={item.originalLocale} contentRevision={item.contentRevision} />
      </div>
      {canEdit ? (
        <button className="mt-1 text-fg-subtle opacity-0 group-hover:opacity-100 focus:opacity-100" aria-label="Edit title" onClick={() => setEditing(true)}>
          <Pencil className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

function Description({ item, canEdit, onSave }: { item: WorkItemView; canEdit: boolean; onSave: (value: string) => Promise<boolean> }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.descriptionMarkdown);
  useUnsavedChanges(editing && value !== item.descriptionMarkdown);
  const submit = () => void onSave(value).then((ok) => ok && setEditing(false));
  return (
    <section>
      <SectionTitle
        actions={
          canEdit && !editing ? (
            <Button variant="ghost" size="sm" onClick={() => (setValue(item.descriptionMarkdown), setEditing(true))}>
              <Pencil /> {t('common.edit')}
            </Button>
          ) : null
        }
      >
        {t('work.detail.description')}
      </SectionTitle>
      {editing ? (
        <div className="flex flex-col gap-2">
          <MarkdownEditor value={value} onChange={setValue} minRows={8} onSubmit={submit} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" variant="primary" onClick={submit}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      ) : item.descriptionMarkdown.trim() ? (
        <TranslatableMarkdown entityKind="WORK_ITEM" entityId={item.id} field="descriptionMarkdown" source={item.descriptionMarkdown} sourceLocale={item.originalLocale} contentRevision={item.contentRevision} />
      ) : (
        <p className="text-sm text-fg-subtle">{t('work.detail.noDescription')}</p>
      )}
    </section>
  );
}

const criterionTone = { OPEN: 'neutral', MET: 'success', NOT_MET: 'danger', WAIVED: 'outline' } as const;

function Criteria({ item, canEdit, onSave }: { item: WorkItemView; canEdit: boolean; onSave: (value: AcceptanceCriterion[]) => Promise<boolean> }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState('');
  useUnsavedChanges(adding.trim() !== '');
  const translated = useTranslatedCriteria(item.id, item.originalLocale, item.acceptanceCriteria, item.contentRevision);
  return (
    <section>
      <SectionTitle actions={translated.note}>{t('work.detail.criteria')}</SectionTitle>
      {item.acceptanceCriteria.length === 0 ? <p className="text-sm text-fg-subtle">{t('work.detail.noCriteria')}</p> : null}
      <ul className="flex flex-col gap-1.5">
        {item.acceptanceCriteria.map((c) => (
          <li key={c.id} className="flex items-start gap-2 rounded-[var(--radius)] border border-border px-3 py-2 text-sm">
            <Badge tone={criterionTone[c.status]} className="mt-0.5">
              {t(`work.detail.criterionStatus.${c.status}`)}
            </Badge>
            <span className="min-w-0 flex-1">{translated.textOf(c)}</span>
            {canEdit && c.status === 'OPEN' ? (
              <button aria-label={t('common.delete')} className="text-fg-subtle hover:text-danger" onClick={() => void onSave(item.acceptanceCriteria.filter((x) => x.id !== c.id))}>
                <X className="size-3.5" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {canEdit ? (
        <form
          className="mt-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!adding.trim()) return;
            void onSave([...item.acceptanceCriteria, { id: `c${Date.now().toString(36)}`, text: adding.trim(), status: 'OPEN', note: null }]).then(() => setAdding(''));
          }}
        >
          <Input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder={t('work.create.criterionPlaceholder')} aria-label={t('work.create.addCriterion')} />
          <Button type="submit" size="md" disabled={!adding.trim()}>
            <Plus /> {t('work.create.addCriterion')}
          </Button>
        </form>
      ) : null}
    </section>
  );
}

function Blockers({ item, canEdit }: { item: WorkItemView; canEdit: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [prevents, setPrevents] = useState(true);
  const set = useApiMutation('setWorkBlocker', { invalidate: ['getWorkItem', 'listWorkItems'] });
  const active = item.blockers.filter((b) => !b.resolvedAt);
  const run = async (body: Parameters<typeof set.mutateAsync>[0]['body']) => {
    try {
      await set.mutateAsync({ params: { id: item.id }, body, idempotencyKey: newIdempotencyKey() });
    } catch (error) {
      toastError(error);
    }
  };
  if (!active.length && !canEdit) return null;
  return (
    <section>
      <SectionTitle
        actions={
          canEdit ? (
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
              <Plus /> {t('work.detail.addBlocker')}
            </Button>
          ) : null
        }
      >
        {t('work.detail.blockers')}
      </SectionTitle>
      <ul className="flex flex-col gap-1.5">
        {active.map((b) => (
          <li key={b.id} className="flex items-start gap-2 rounded-[var(--radius)] border border-danger/30 bg-danger-soft/40 px-3 py-2 text-sm">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-danger" />
            <div className="min-w-0 flex-1">
              <p>{b.reason}</p>
              <p className="text-xs text-fg-subtle">
                {b.openedBy?.displayName} · <TimeAgo iso={b.openedAt} />
                {b.preventsCompletion ? ` · ${t('work.detail.preventsCompletion')}` : ''}
                {b.dependencyWorkItem ? ` · ${b.dependencyWorkItem.key}` : ''}
              </p>
            </div>
            {canEdit ? (
              <Button size="sm" variant="secondary" onClick={() => void run({ action: 'RESOLVE', blockerId: b.id, expectedVersion: b.version, reason: 'Resolved' })}>
                {t('work.detail.resolveBlocker')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={t('work.detail.addBlocker')}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await run({ action: 'OPEN', reason, preventsCompletion: prevents });
              setReason('');
              setOpen(false);
            }}
            className="flex flex-col gap-3"
          >
            <Field label={t('work.detail.blockerReason')} htmlFor="blocker-reason" required>
              <Textarea id="blocker-reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={prevents} onCheckedChange={(v) => setPrevents(v === true)} /> {t('work.detail.preventsCompletion')}
            </label>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={!reason.trim()} loading={set.isPending}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** Direct sub-items with progress; unlinking keeps both items. */
function SubItems({ item, canEdit, onAdd }: { item: WorkItemView; canEdit: boolean; onAdd: () => void }) {
  const { t } = useTranslation();
  const remove = useApiMutation('removeWorkRelation', { invalidate: ['getWorkItem', 'listWorkItems'] });
  const { children, progress } = item.hierarchy;
  if (!children.length && !canEdit && item.type !== 'EPIC') return null;
  return (
    <section>
      <SectionTitle
        actions={
          canEdit ? (
            <Button variant="ghost" size="sm" onClick={onAdd}>
              <Plus /> {t('work.detail.addSubItem')}
            </Button>
          ) : null
        }
      >
        <span className="flex items-center gap-2">
          {t('work.detail.subItems')}
          {progress ? <ProgressPill progress={progress} /> : null}
        </span>
      </SectionTitle>
      {children.length ? (
        <ul className="flex flex-col divide-y divide-border rounded-[var(--radius)] border border-border">
          {children.map((c) => (
            <li key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
              <KeyLabel value={c.key} />
              <Link to={`/work/${c.key}`} className="min-w-0 flex-1 truncate hover:underline">
                {c.title}
              </Link>
              {c.type === 'EPIC' ? <Badge tone="accent">{t('status.workType.EPIC')}</Badge> : null}
              {c.progress ? <ProgressPill progress={c.progress} /> : null}
              {c.requiresCompletion ? <Badge tone="outline">{t('work.detail.requiresCompletion')}</Badge> : null}
              {c.assignee ? <UserChip user={c.assignee} link /> : null}
              <PhaseBadge phase={c.phase} />
              {canEdit ? (
                <button aria-label={t('work.detail.unlinkSubItem')} title={t('work.detail.unlinkSubItem')} className="text-fg-subtle hover:text-danger" onClick={() => void remove.mutateAsync({ params: { id: item.id, childId: c.relationId } }).catch(toastError)}>
                  <X className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-subtle">{t('work.detail.noSubItems')}</p>
      )}
    </section>
  );
}

function Relations({ item, canEdit }: { item: WorkItemView; canEdit: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<RelationKind>('RELATES_TO');
  const [required, setRequired] = useState(false);
  const add = useApiMutation('addWorkRelation', { invalidate: ['getWorkItem'] });
  const remove = useApiMutation('removeWorkRelation', { invalidate: ['getWorkItem'] });
  const relations = item.relations.filter((r) => !(r.kind === 'PARENT_OF' && r.direction === 'OUTGOING'));
  if (!relations.length && !canEdit) return null;
  return (
    <section>
      <SectionTitle
        actions={
          canEdit ? (
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
              <Link2 /> {t('work.detail.addRelation')}
            </Button>
          ) : null
        }
      >
        {t('work.detail.relations')}
      </SectionTitle>
      <ul className="flex flex-col gap-1">
        {relations.map((r) => (
          <li key={r.id} className="flex items-center gap-2 text-sm">
            <span className="w-28 shrink-0 text-xs text-fg-subtle">{r.direction === 'OUTGOING' ? t(`work.detail.relationKinds.${r.kind}`) : r.kind === 'CAUSED_BY' ? t('work.detail.defect') : `← ${t(`work.detail.relationKinds.${r.kind}`)}`}</span>
            <KeyLabel value={r.other.key} />
            <Link to={`/work/${r.other.key}`} className="min-w-0 flex-1 truncate hover:underline">
              {r.other.title}
            </Link>
            <PhaseBadge phase={r.other.phase} />
            {r.requiresCompletion ? <Badge tone="outline">{t('work.detail.requiresCompletion')}</Badge> : null}
            {canEdit ? (
              <button aria-label={t('common.delete')} className="text-fg-subtle hover:text-danger" onClick={() => void remove.mutateAsync({ params: { id: item.id, childId: r.id } }).catch(toastError)}>
                <X className="size-3.5" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={t('work.detail.addRelation')}>
          <div className="flex flex-col gap-3">
            <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} aria-label="Relation">
              {(['RELATES_TO', 'PARENT_OF', 'DEPENDS_ON', 'DUPLICATES', ...(item.type === 'BUG' ? (['CAUSED_BY'] as const) : [])] as const).map((k) => (
                <option key={k} value={k}>
                  {t(`work.detail.relationKinds.${k}`)}
                </option>
              ))}
            </NativeSelect>
            {kind === 'PARENT_OF' || kind === 'DEPENDS_ON' ? (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={required} onCheckedChange={(v) => setRequired(v === true)} /> {t('work.detail.requiresCompletion')}
              </label>
            ) : null}
            <WorkSearchList
              excludeId={item.id}
              placeholder={t('requests.detail.linkWorkSearch')}
              autoFocus
              {...(kind === 'CAUSED_BY' ? { archived: 'any' as const } : {})}
              onPick={async (w) => {
                try {
                  await add.mutateAsync({ params: { id: item.id }, body: { kind, targetWorkItemId: w.id, requiresCompletion: required }, idempotencyKey: newIdempotencyKey() });
                  setOpen(false);
                } catch (error) {
                  toastError(error);
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** Provisional agent-created work: confirm it as new work, or merge it into an existing item. */
function ProvisionalReview({ item, canReview }: { item: WorkItemView; canReview: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const review = useApiMutation('reviewWorkAssociation', { invalidate: ['getWorkItem', 'listWorkItems', 'listExceptions'] });
  const [merging, setMerging] = useState(false);
  const [q, setQ] = useState('');
  const [target, setTarget] = useState<{ id: string; key: string; title: string } | null>(null);
  const [reason, setReason] = useState('');
  const search = useApiQuery('listWorkItems', { query: { q, limit: '8', associationStatus: 'CONFIRMED' } }, { enabled: merging && q.trim().length >= 2 });
  const confirm = async () => {
    try {
      await review.mutateAsync({ params: { id: item.id }, body: { decision: 'CONFIRM', expectedVersion: item.version }, idempotencyKey: newIdempotencyKey() });
      toast.success(t('work.provisional.confirmed'));
    } catch (error) {
      toastError(error);
    }
  };
  const merge = async () => {
    if (!target) return;
    try {
      const result = await review.mutateAsync({ params: { id: item.id }, body: { decision: 'MERGE', expectedVersion: item.version, targetWorkItemId: target.id, reason: reason.trim() }, idempotencyKey: newIdempotencyKey() });
      toast.success(t('work.provisional.merged', { key: target.key }));
      setMerging(false);
      void navigate(`/work/${result.data.key}`);
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-[var(--radius)] bg-warning-soft px-3 py-2 text-xs text-warning">
      <span className="min-w-0 flex-1">{t('work.detail.provisionalNote')}</span>
      {canReview ? (
        <>
          <Button size="sm" variant="secondary" loading={review.isPending && !merging} onClick={() => void confirm()}>
            <Check /> {t('work.provisional.confirm')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMerging(true)}>
            {t('work.provisional.merge')}
          </Button>
        </>
      ) : null}
      <Dialog open={merging} onOpenChange={setMerging}>
        <DialogContent title={t('work.provisional.mergeTitle')} description={t('work.provisional.mergeHint')} closeLabel={t('common.close')}>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void merge();
            }}
          >
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('requests.detail.linkWorkSearch')} aria-label={t('requests.detail.linkWorkSearch')} autoFocus />
            <ul className="max-h-56 overflow-y-auto" role="listbox" aria-label={t('work.provisional.mergeTitle')}>
              {(search.data?.data ?? [])
                .filter((w) => w.id !== item.id)
                .map((w) => (
                  <li key={w.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={target?.id === w.id}
                      onClick={() => setTarget({ id: w.id, key: w.key, title: w.title })}
                      className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${target?.id === w.id ? 'bg-accent-soft' : ''}`}
                    >
                      <KeyLabel value={w.key} />
                      <span className="truncate">{w.title}</span>
                    </button>
                  </li>
                ))}
            </ul>
            <Field label={t('common.reason')} htmlFor="merge-reason" required>
              <Textarea id="merge-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setMerging(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={review.isPending} disabled={!target || !reason.trim()}>
                {target ? t('work.provisional.mergeInto', { key: target.key }) : t('work.provisional.merge')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Properties({
  item,
  onPatch,
  prioritySelect,
  assignOpen,
  onAssignOpenChange,
  labelsOpen,
  onLabelsOpenChange,
}: {
  item: WorkItemView;
  onPatch: (body: Record<string, unknown>) => Promise<boolean>;
  prioritySelect: React.RefObject<HTMLSelectElement | null>;
  assignOpen: boolean;
  onAssignOpenChange: (open: boolean) => void;
  labelsOpen: boolean;
  onLabelsOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const can = (a: string) => item.availableActions.includes(a);
  const [reposOpen, setReposOpen] = useState(false);
  const customFields = useCustomFields();
  return (
    <aside className="border-t border-border bg-surface-2 px-5 py-4 @4xl:border-t-0 @4xl:border-l">
      <SectionTitle>{t('work.detail.properties')}</SectionTitle>
      <dl>
        <PropertyRow label={t('work.columns.priority')}>
          {can('prioritize') ? (
            <NativeSelect ref={prioritySelect} value={item.priority} onChange={(e) => void onPatch({ priority: e.target.value })} aria-label={t('work.columns.priority')} className="h-7">
              {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
                <option key={p} value={p}>
                  {t(`status.priority.${p}`)}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <PriorityLabel priority={item.priority} />
          )}
        </PropertyRow>
        <PropertyRow label={t('work.columns.assignee')}>
          {can('assign') ? (
            <PrincipalPicker
              label={t('work.columns.assignee')}
              value={item.assignee ? { id: item.assignee.id, name: item.assignee.displayName } : null}
              onChange={(p) => void onPatch({ assigneeId: p?.id ?? null })}
              open={assignOpen}
              onOpenChange={onAssignOpenChange}
            />
          ) : (
            <UserChip user={item.assignee} link />
          )}
        </PropertyRow>
        {item.completionPolicy === 'REVIEWED_DELIVERABLE' || item.reviewer ? (
          <PropertyRow label={t('work.detail.reviewer')}>
            {can('edit') || can('assign') ? (
              <PrincipalPicker label={t('work.detail.reviewer')} value={item.reviewer ? { id: item.reviewer.id, name: item.reviewer.displayName } : null} onChange={(p) => void onPatch({ reviewerId: p?.id ?? null })} placeholder={t('common.none')} />
            ) : (
              <UserChip user={item.reviewer} empty={t('common.none')} link />
            )}
          </PropertyRow>
        ) : null}
        <PropertyRow label={t('work.detail.startDate')}>
          <DateField value={item.startDate} max={item.dueDate} canEdit={can('edit') || can('assign')} label={t('work.detail.startDate')} onChange={(startDate) => void onPatch({ startDate })} render={(d) => <span className="text-sm">{formatCalendarDate(d)}</span>} />
        </PropertyRow>
        <PropertyRow label={t('work.detail.dueDate')}>
          <DateField value={item.dueDate} min={item.startDate} canEdit={can('edit') || can('assign')} label={t('work.detail.dueDate')} onChange={(dueDate) => void onPatch({ dueDate })} render={(d) => <DueLabel date={d} phase={item.phase} />} />
        </PropertyRow>
        <PropertyRow label={t('work.detail.owner')}>
          <UserChip user={item.owner} empty={item.team?.name ?? t('common.none')} link />
        </PropertyRow>
        <PropertyRow label={t('work.columns.team')}>{item.team ? <Link className="hover:underline" to={`/teams/${item.team.id}`}>{item.team.name}</Link> : <span className="text-fg-subtle">—</span>}</PropertyRow>
        <PropertyRow label={t('work.columns.project')}>{item.project ? <Link className="hover:underline" to={`/projects/${item.project.id}`}>{item.project.name}</Link> : <span className="text-fg-subtle">—</span>}</PropertyRow>
        <PropertyRow label={t('work.columns.type')}>
          {can('edit') ? (
            <NativeSelect value={item.type} onChange={(e) => void onPatch({ type: e.target.value })} className="h-7" aria-label={t('work.columns.type')}>
              {WORK_TYPES.map((v) => (
                <option key={v} value={v}>
                  {t(`status.workType.${v}`)}
                </option>
              ))}
            </NativeSelect>
          ) : (
            t(`status.workType.${item.type}`)
          )}
        </PropertyRow>
        {item.defectCause ? (
          <PropertyRow label={t('work.defectCause.title')}>
            <CauseValue item={item} canEdit={can('recordCause')} />
          </PropertyRow>
        ) : null}
        <PropertyRow label={t('languageCorrection.label')}>
          <OriginalLanguage value={item.originalLocale} canEdit={can('edit')} onChange={(originalLocale) => void onPatch({ originalLocale })} />
        </PropertyRow>
        <PropertyRow label={t('work.create.policy')}>
          <Tooltip content={t(`status.policyHelp.${item.completionPolicy}`)}>
            <span className="text-sm underline decoration-dotted underline-offset-2">{t(`status.policy.${item.completionPolicy}`)}</span>
          </Tooltip>
        </PropertyRow>
        {item.completionBasis ? <PropertyRow label={t('work.columns.delivery')}>{t(`status.completionBasis.${item.completionBasis}`)}</PropertyRow> : null}
        <PropertyRow label={t('work.detail.labels')}>
          <LabelsEditor labels={item.labels} canEdit={can('edit')} open={labelsOpen} onOpenChange={onLabelsOpenChange} onSave={(labels) => onPatch({ labels })} />
        </PropertyRow>
        {fieldsFor(customFields, item.project?.id).map((field) => (
          <PropertyRow key={field.id} label={field.name}>
            {(can('edit') || can('assign')) && !item.archivedAt ? (
              <FieldEditor field={field} value={item.customFields[field.id]} onCommit={(value) => void onPatch({ customFields: { [field.id]: value } })} className="w-full" />
            ) : (
              <FieldValue field={field} value={item.customFields[field.id]} />
            )}
          </PropertyRow>
        ))}
        <PropertyRow label={t('work.detail.repositories')}>
          <div className="flex items-start gap-1">
            {item.repositories.length ? (
              <ul className="flex min-w-0 flex-1 flex-col gap-0.5">
                {item.repositories.map((r) => (
                  <li key={r.id} className="flex items-center gap-1 truncate text-xs" title={r.fullName}>
                    <GitBranch className="size-3 shrink-0 text-fg-subtle" /> <span className="truncate">{r.fullName.split('/').pop()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="flex-1 text-xs text-fg-subtle">{t('work.detail.noRepositories')}</span>
            )}
            {can('edit') ? (
              <Button variant="ghost" size="iconSm" aria-label={t('repoPicker.edit')} onClick={() => setReposOpen(true)}>
                <Pencil />
              </Button>
            ) : null}
          </div>
          {reposOpen ? <RepositoryDialog title={t('repoPicker.workTitle')} description={t('repoPicker.workHint')} initial={item.repositories} onSave={(repositoryIds) => onPatch({ repositoryIds })} onClose={() => setReposOpen(false)} /> : null}
        </PropertyRow>
        {item.contributors.length ? (
          <PropertyRow label={t('work.detail.contributors')}>
            <div className="flex flex-wrap gap-1">
              {item.contributors.map((c) => (
                <Tooltip key={c.user.id} content={`${c.user.displayName}: ${c.types.join(', ')}`}>
                  <span>
                    <UserChip user={c.user} link />
                  </span>
                </Tooltip>
              ))}
            </div>
          </PropertyRow>
        ) : null}
        <PropertyRow label={t('common.created')}>
          <span className="text-xs text-fg-muted" title={formatDateTime(item.createdAt)}>
            <TimeAgo iso={item.createdAt} /> {item.createdBy ? t('common.by', { name: item.createdBy.displayName }) : ''}
          </span>
        </PropertyRow>
        <PropertyRow label={t('common.updated')}>
          <span className="text-xs">
            <TimeAgo iso={item.updatedAt} />
          </span>
        </PropertyRow>
      </dl>
      <ShortcutHelp />
    </aside>
  );
}

/** Calendar-day property: a native date input when editable, with a clear button. */
function DateField({ value, min, max, canEdit, label, onChange, render }: { value: string | null; min?: string | null; max?: string | null; canEdit: boolean; label: string; onChange: (value: string | null) => void; render: (date: string) => React.ReactNode }) {
  const { t } = useTranslation();
  if (!canEdit) return value ? <>{render(value)}</> : <span className="text-fg-subtle">—</span>;
  return (
    <div className="flex items-center gap-1">
      <DateInput className="h-7 min-w-0 flex-1" aria-label={label} value={value} min={min ?? undefined} max={max ?? undefined} onCommit={onChange} />
      {value ? (
        <Button variant="ghost" size="iconSm" aria-label={t('work.detail.clearDate')} title={t('work.detail.clearDate')} onClick={() => onChange(null)}>
          <X />
        </Button>
      ) : null}
    </div>
  );
}

/** Shortcut reminder for the item hotkeys bound in the detail view. */
function ShortcutHelp() {
  const { t } = useTranslation();
  const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
  const rows: Array<[string, string]> = [
    ['P', t('work.detail.shortcuts.priority')],
    ['A', t('work.detail.shortcuts.assign')],
    ['I', t('work.detail.shortcuts.assignMe')],
    ['L', t('work.detail.shortcuts.labels')],
    [`${mod} .`, t('work.detail.shortcuts.copyKey')],
  ];
  return (
    <details className="mt-4 text-xs text-fg-subtle">
      <summary className="flex cursor-pointer items-center gap-1.5 select-none hover:text-fg">
        <Keyboard className="size-3.5" /> {t('work.detail.shortcuts.title')}
      </summary>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.map(([key, label]) => (
          <div key={key} className="contents">
            <dt>
              <Kbd>{key}</Kbd>
            </dt>
            <dd>{label}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** Label chips with an editor that suggests labels already used on readable work. */
function LabelsEditor({ labels, canEdit, open, onOpenChange, onSave }: { labels: string[]; canEdit: boolean; open: boolean; onOpenChange: (open: boolean) => void; onSave: (labels: string[]) => Promise<boolean> }) {
  const { t } = useTranslation();
  const me = useMe();
  const catalog = useLabelCatalog();
  const [managing, setManaging] = useState(false);
  const [draft, setDraft] = useState('');
  const suggestions = useApiQuery('listWorkLabels', { query: draft.trim() ? { q: draft.trim().toLowerCase() } : {} }, { enabled: open });
  const add = (label: string) => {
    const value = label.trim().toLowerCase();
    setDraft('');
    if (!value || labels.includes(value)) return;
    void onSave([...labels, value]);
  };
  const remove = (label: string) => void onSave(labels.filter((l) => l !== label));
  const chips = labels.length ? (
    <div className="flex flex-wrap gap-1">
      {labels.map((l) => (
        <LabelBadge key={l} label={l} catalog={catalog}>
          {canEdit ? (
            <button type="button" aria-label={t('common.removeItem', { name: l })} className="text-fg-subtle hover:text-danger" onClick={() => remove(l)}>
              <X className="size-3" />
            </button>
          ) : null}
        </LabelBadge>
      ))}
    </div>
  ) : (
    <span className="text-fg-subtle">—</span>
  );
  if (!canEdit) return chips;
  const typed = draft.trim().toLowerCase();
  const fromCatalog = [...catalog.keys()].filter((name) => !typed || name.includes(typed)).map((label) => ({ label, count: 0 }));
  const options = [...(suggestions.data?.data ?? []), ...fromCatalog].filter((s, i, all) => !labels.includes(s.label) && all.findIndex((x) => x.label === s.label) === i).slice(0, 8);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <div className="flex items-start gap-1">
          <div className="min-w-0 flex-1">{chips}</div>
          <Button variant="ghost" size="iconSm" aria-label={t('work.detail.editLabels')} onClick={() => onOpenChange(true)}>
            <Pencil />
          </Button>
        </div>
      </PopoverAnchor>
      <PopoverContent align="end" className="w-64 p-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add(draft);
          }}
        >
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={40} autoFocus placeholder={t('work.detail.labelsPlaceholder')} aria-label={t('work.detail.editLabels')} />
        </form>
        {options.length ? (
          <ul className="mt-1 max-h-48 overflow-y-auto">
            {options.map((o) => (
              <li key={o.label}>
                <button type="button" className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-muted" onClick={() => add(o.label)}>
                  <LabelBadge label={o.label} catalog={catalog} />
                  {o.count ? <span className="text-xs text-fg-subtle">{o.count}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {me.permissions.canManageLabels ? (
          <button type="button" className="mt-1 w-full border-t border-border px-2 pt-1.5 text-left text-xs text-accent hover:underline" onClick={() => (onOpenChange(false), setManaging(true))}>
            {t('labels.manage')}
          </button>
        ) : null}
      </PopoverContent>
      {managing ? <LabelManagerDialog onClose={() => setManaging(false)} suggestions={(suggestions.data?.data ?? []).map((s) => s.label)} /> : null}
    </Popover>
  );
}

/** Administrator-only permanent deletion: type the key and give a reason (kept in the audit log). */
function DeleteWorkDialog({ item, onClose }: { item: WorkItemView; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const remove = useApiMutation('deleteWorkItem', { invalidate: ['listWorkItems', 'getWorkItem'] });
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const submit = async () => {
    try {
      await remove.mutateAsync({ params: { id: item.id }, body: { expectedVersion: item.version, reason: reason.trim() } });
      toast.success(t('work.detail.deletedToast', { key: item.key }));
      onClose();
      // Opened in a drawer (work list or another page): close it; on the full page go back to the list.
      const drawerParam = ['selected', 'peekWork'].find((k) => [item.id, item.key].includes(params.get(k) ?? ''));
      if (drawerParam) {
        const next = new URLSearchParams(params);
        next.delete(drawerParam);
        setParams(next);
      } else void navigate('/work');
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t('work.detail.deleteTitle', { key: item.key })} description={t('work.detail.deleteHint')} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={t('common.reason')} htmlFor="delete-reason" required>
            <Textarea id="delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} />
          </Field>
          <Field label={t('work.detail.deleteConfirm', { key: item.key })} htmlFor="delete-key" required>
            <Input id="delete-key" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="danger" loading={remove.isPending} disabled={typed.trim().toUpperCase() !== item.key || !reason.trim()}>
              <Trash2 /> {t('work.detail.delete')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProgressList({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useApiQuery('getWorkProgress', { params: { id }, query: {} }, { refetchInterval: POLL_ACTIVE });
  return (
    <QueryState query={query} empty={query.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">{t('work.detail.timelineEmpty')}</p> : undefined}>
      <ol className="flex flex-col gap-3">
        {(query.data?.data ?? []).map((entry) => (
          <li key={entry.id} className="rounded-[var(--radius)] border border-border px-3 py-2">
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
              <Badge tone="accent">{t(`status.milestone.${entry.milestoneType}`)}</Badge>
              {entry.client ? (
                <Badge tone="neutral">
                  <Bot /> {t(`status.client.${entry.client}`)}
                </Badge>
              ) : null}
              <span>{entry.actor?.displayName}</span>
              <TimeAgo iso={entry.occurredAt} />
            </div>
            <TranslatableMarkdown entityKind="PROGRESS_ENTRY" entityId={entry.id} field="summaryMarkdown" source={entry.summaryMarkdown} sourceLocale="und" />
            {entry.checks.length ? (
              <ul className="mt-2 flex flex-wrap gap-1">
                {entry.checks.map((c, i) => (
                  <li key={i}>
                    <Badge tone={c.result === 'PASSED' ? 'success' : c.result === 'FAILED' ? 'danger' : 'neutral'} title={c.detail}>
                      {c.name}: {c.result} · {c.claimBasis === 'CI_OBSERVED' ? t('status.basis.PROVIDER_VERIFIED') : t('status.basis.AGENT_REPORTED')}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </QueryState>
  );
}

function Episodes({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useApiQuery('getWorkEpisodes', { params: { id } });
  const episodes = query.data?.data ?? [];
  if (episodes.length <= 1 && !episodes[0]?.completedAt) return null;
  return (
    <section className="mt-6">
      <SectionTitle>{t('work.detail.episodes')}</SectionTitle>
      <ol className="flex flex-col gap-1 text-sm">
        {episodes.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-2">
            <Badge tone="outline">#{e.episode}</Badge>
            {e.completedAt ? <span>✓ {formatDateTime(e.completedAt)}</span> : <span className="text-fg-subtle">…</span>}
            {e.completionBasis ? <span className="text-xs text-fg-subtle">{t(`status.completionBasis.${e.completionBasis}`)}</span> : null}
            {e.reopenedAt ? <span className="text-xs text-danger">↺ {e.reopenReason}</span> : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Guarded transition dialog: collects reasons, outcomes, criteria dispositions or reviewers. */
export function TransitionDialog({ item, action, onClose }: { item: WorkItemView; action: WorkAction; onClose: () => void }) {
  const { t } = useTranslation();
  const transition = useApiMutation('transitionWorkItem', { invalidate: ['getWorkItem', 'listWorkItems', 'getWorkEpisodes'] });
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState(item.outcomeMarkdown ?? '');
  const [criteria, setCriteria] = useState(() => Object.fromEntries(item.acceptanceCriteria.map((c) => [c.id, c.status === 'OPEN' ? 'MET' : c.status])) as Record<string, string>);
  const [reviewer, setReviewer] = useState<{ id: string; name: string } | null>(item.reviewer ? { id: item.reviewer.id, name: item.reviewer.displayName } : null);
  const needsReason = ['cancel', 'reopen', 'request_changes', 'close_without_evidence'].includes(action);
  const needsOutcome = action === 'complete_deliverable' || (action === 'request_review' && item.completionPolicy === 'REVIEWED_DELIVERABLE');
  const needsReviewer = action === 'request_review' && item.completionPolicy === 'REVIEWED_DELIVERABLE';
  // A person closing a bug names its cause (ADR-0059); a bug that already has one is not asked again.
  const needsCause = item.defectCause === 'NOT_INFORMED' && completesWork(action);
  const [cause, setCause] = useState(() => emptyCause(item));
  const defectCause = needsCause ? causeInput(cause) : null;
  const simple = !needsReason && !needsOutcome && !needsReviewer && !needsCause;

  const submit = async () => {
    try {
      await transition.mutateAsync({
        params: { id: item.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          expectedVersion: item.version,
          action,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(needsReviewer && reviewer ? { reviewerId: reviewer.id } : {}),
          ...(defectCause ? { defectCause } : {}),
          ...(needsOutcome
            ? {
                deliverable: {
                  outcomeMarkdown: outcome,
                  criteria: item.acceptanceCriteria.map((c) => ({ id: c.id, status: (criteria[c.id] ?? 'MET') as 'MET' })),
                },
              }
            : {}),
        },
      });
      toast.success(t(`actions.work.${action}`));
      onClose();
    } catch (error) {
      toastError(error);
    }
  };

  // Transitions without inputs run immediately. StrictMode re-runs mount effects in development,
  // so a ref keeps this to exactly one request per opened dialog.
  const fired = useRef(false);
  useEffect(() => {
    if (!simple || fired.current) return;
    fired.current = true;
    void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (simple) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t(`actions.work.${action}`)} description={action === 'complete_deliverable' ? t(`status.policyHelp.${item.completionPolicy}`) : action === 'close_without_evidence' ? t('noEvidence.dialogHint') : undefined}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="flex flex-col gap-4"
        >
          {needsReviewer ? (
            <Field label={t('work.detail.reviewer')} required>
              <PrincipalPicker label={t('work.detail.reviewer')} value={reviewer} onChange={(p) => setReviewer(p ? { id: p.id, name: p.name } : null)} allowClear={false} />
            </Field>
          ) : null}
          {needsOutcome ? (
            <>
              <Field label={t('work.detail.outcome')} required>
                <MarkdownEditor value={outcome} onChange={setOutcome} placeholder={t('work.detail.outcomePlaceholder')} minRows={4} onSubmit={() => void submit()} />
              </Field>
              {item.acceptanceCriteria.length ? (
                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-xs font-medium text-fg-muted">{t('work.detail.criteria')}</legend>
                  {item.acceptanceCriteria.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-sm">
                      <NativeSelect value={criteria[c.id]} onChange={(e) => setCriteria((s) => ({ ...s, [c.id]: e.target.value }))} className="h-7 w-32" aria-label={c.text}>
                        {(['MET', 'NOT_MET', 'WAIVED'] as const).map((s) => (
                          <option key={s} value={s}>
                            {t(`work.detail.criterionStatus.${s}`)}
                          </option>
                        ))}
                      </NativeSelect>
                      <span className="min-w-0 flex-1">{c.text}</span>
                    </div>
                  ))}
                </fieldset>
              ) : null}
            </>
          ) : null}
          {needsReason ? (
            <Field label={t('common.reason')} required>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('common.reasonPlaceholder')} autoFocus />
            </Field>
          ) : null}
          {needsCause ? <CauseFields item={item} value={cause} onChange={setCause} /> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant={action === 'cancel' ? 'danger' : 'primary'} loading={transition.isPending} disabled={(needsReason && !reason.trim()) || (needsOutcome && !outcome.trim()) || (needsReviewer && !reviewer) || (needsCause && !defectCause)}>
              {t(`actions.work.${action}`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { MarkdownView };
